import React, { useState, useEffect, useRef } from "react";
import { Card, Snackbar, Alert, Box, Typography } from "@mui/material";
import { CheckCircle, Warning, Error as ErrorIcon, Info } from "@mui/icons-material";

import DashboardLayout from "app/layout/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/layout/DashboardNavbar";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";

import TableUsuariosParqueadero from "./components/TableAdmin/TableUsuariosParqueadero";
import { useUsuariosParqueadero } from "./hooks/useUsuariosParqueadero";
import HeaderAdmin from "./components/HeaderAdmin";
import logoImage from "../../../assets/images/logo horinzontal.png";

const AdminUsuarios = () => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
    autoHideDuration: 6000,
  });

  const tableRef = useRef(null);

  const {
    usuarios,
    allUsuarios,
    loading: loadingUsuarios,
    error: errorUsuarios,
    stats: usuariosStats,
    currentUser,
    commerces,
    currentCommerceId,
    currentCommerceName,
    hasPermission,
    permisosUsuarios,
    changeCommerce,
    addUsuario,
    editUsuario,
    deleteUsuario,
    refreshData: refreshUsuarios,
    applyFilters,
    resetFilters,
    hasData: hasUsuariosData,
  } = useUsuariosParqueadero();

  const isFirstLoad = useRef(true);
  const hasShownInitialMessage = useRef(false);

  const showNotification = (message, severity = "success", duration = 6000) => {
    setSnackbar({ open: true, message, severity, autoHideDuration: duration });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleRefresh = async () => {
    try {
      showNotification("Actualizando datos...", "info", 2000);
      await refreshUsuarios();
      showNotification("Datos actualizados correctamente", "success");
    } catch (err) {
      showNotification(`Error al actualizar: ${err.message}`, "error");
    }
  };

  const handleAddClick = () => {
    if (tableRef.current) {
      tableRef.current.openAddModal();
    }
  };

  const handleCommerceChange = async (commerceId, commerceName) => {
    try {
      showNotification(`Cambiando a ${commerceName}...`, "info", 2000);
      await changeCommerce(commerceId, commerceName);
      showNotification(`Propiedad cambiada a ${commerceName}`, "success");
    } catch (error) {
      showNotification(`Error al cambiar propiedad: ${error.message}`, "error");
    }
  };

  const handleAddUsuario = async (newUsuario) => {
    try {
      const result = await addUsuario(newUsuario);
      if (result && result.success) {
        showNotification("Usuario agregado exitosamente", "success");
        return result;
      } else {
        const errorMsg = result?.error || "Error al agregar usuario";
        showNotification(errorMsg, "error");
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      console.error("❌ Error en handleAddUsuario:", error);
      showNotification("Error al agregar usuario", "error");
      return { success: false, error: error.message };
    }
  };

  const handleEditUsuario = async (updatedUsuario) => {
    try {
      const result = await editUsuario(updatedUsuario);
      if (result && result.success) {
        showNotification("Usuario actualizado exitosamente", "success");
        return result;
      } else {
        const errorMsg = result?.error || "Error al actualizar usuario";
        showNotification(errorMsg, "error");
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      console.error("❌ Error en handleEditUsuario:", error);
      showNotification("Error al actualizar usuario", "error");
      return { success: false, error: error.message };
    }
  };

  const handleDeleteUsuario = async (id, plate) => {
    try {
      const result = await deleteUsuario(id, plate);
      if (result && result.success) {
        showNotification("Usuario eliminado exitosamente", "success");
        return result;
      } else {
        const errorMsg = result?.error || "Error al eliminar usuario";
        showNotification(errorMsg, "error");
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      console.error("❌ Error en handleDeleteUsuario:", error);
      showNotification("Error al eliminar usuario", "error");
      return { success: false, error: error.message };
    }
  };

  useEffect(() => {
    if (errorUsuarios) {
      showNotification(errorUsuarios, "error", 8000);
    }
  }, [errorUsuarios]);

  useEffect(() => {
    if (
      isFirstLoad.current &&
      !loadingUsuarios &&
      hasUsuariosData &&
      !errorUsuarios &&
      !hasShownInitialMessage.current
    ) {
      hasShownInitialMessage.current = true;
      showNotification(`Cargados ${usuarios.length} usuarios`, "info", 5000);
    }
  }, [loadingUsuarios, hasUsuariosData, errorUsuarios, usuarios.length]);

  useEffect(() => {
    if (isFirstLoad.current && !loadingUsuarios) {
      isFirstLoad.current = false;
    }
  }, [loadingUsuarios]);

  // Si no tiene permisos, mostrar mensaje con logo
  if (!hasPermission && !loadingUsuarios) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <Card
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: "12px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            <Box
              component="img"
              src={logoImage}
              alt="GoPass"
              sx={{
                height: 80,
                width: "auto",
                objectFit: "contain",
                mb: 3,
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            <Typography variant="h5" fontWeight="bold" color="text.secondary" gutterBottom>
              No tiene permisos para ver este módulo
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Contacte al administrador del sistema para solicitar acceso.
            </Typography>
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.autoHideDuration}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
          sx={{ width: "100%", maxWidth: "500px", borderRadius: "8px" }}
          iconMapping={{
            success: <CheckCircle fontSize="inherit" />,
            error: <ErrorIcon fontSize="inherit" />,
            warning: <Warning fontSize="inherit" />,
            info: <Info fontSize="inherit" />,
          }}
        >
          <MDTypography variant="body2" fontWeight="medium">
            {snackbar.message}
          </MDTypography>
        </Alert>
      </Snackbar>

      <HeaderAdmin
        onRefresh={handleRefresh}
        loading={loadingUsuarios}
        usuariosStats={usuariosStats}
        onAddClick={handleAddClick}
        commerceName={currentCommerceName}
        commerces={commerces}
        onCommerceChange={handleCommerceChange}
        hasCreatePermission={permisosUsuarios.crear}
      />

      <MDBox py={3}>
        <Card
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 4px 20px 0 rgba(0,0,0,0.1)",
            border: errorUsuarios ? "1px solid #f44336" : "1px solid #e0e0e0",
          }}
        >
          <MDBox p={3} bgcolor="background.paper">
            <TableUsuariosParqueadero
              ref={tableRef}
              usuarios={usuarios}
              loading={loadingUsuarios}
              error={errorUsuarios}
              onRefresh={refreshUsuarios}
              onAddUsuario={handleAddUsuario}
              onEditUsuario={handleEditUsuario}
              onDeleteUsuario={handleDeleteUsuario}
              currentUser={currentUser}
              onFilterChange={applyFilters}
              onResetFilters={resetFilters}
              hasEditPermission={permisosUsuarios.editar}
              hasDeletePermission={permisosUsuarios.eliminar}
              hasCreatePermission={permisosUsuarios.crear}
            />
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
};

export default AdminUsuarios;
